
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Tag,
  Dropdown,
  Checkbox,
  Icon,
  Input,
} from 'tea-component';

import DOMPurify from 'dompurify';
import './index.scss';
import { useAigcIntl } from '../../utils/utils';

interface TagOption {
  value: number;
  text: string;
  text_en?: string;
  text_cn?: string;
}

interface TagDropdownProps {
  options?: TagOption[];
  tagValue?: number[];
  setTagValue?: (value: number[]) => void;
  placeholder?: string;
  check?: () => void;
}

/**
 * 使用match方法实现模糊查询
 * @param  {Array}  list     进行查询的数组
 * @param  {String} keyWord  查询的关键词
 * @return {Array}           查询的结果
 */
const fuzzyQuery = (list: TagOption[], keyWord: string): TagOption[] => {
  const arr: TagOption[] = [];
  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (const item of list) {
    if (item?.text?.toLowerCase()?.includes(keyWord?.toLowerCase())) {
      arr.push(item);
    }
  }
  return arr;
};

/**
 * 标记指定关键字高亮（以关键字的特殊字符分隔后的数组单个值为单位，不区分大小写）
 * 使用 DOMPurify 净化输入，防止 XSS 攻击
 * @param {string} text 需要处理的原字符串
 * @param {string} key  需要标记的字符串关键字
 */
export function keyFilter(text: string, key: string): React.ReactNode {
  if (key && text && typeof text === 'string' && typeof key === 'string') {
    const sanitizedKey = DOMPurify.sanitize(key);
    const sanitizedText = DOMPurify.sanitize(text);
    const escapedKey = sanitizedKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const newText = sanitizedText.replace(
      new RegExp(escapedKey, 'ig'),
      (str) => `<span style="color: #006EFF;">${str}</span>`,
    );
    return <span dangerouslySetInnerHTML={{ __html: newText }} />;
  }
  return text;
}

export default function TagDropdown(props: TagDropdownProps) {
  const { options = [], tagValue, setTagValue, placeholder, check } = props;
  const { isEn } = useAigcIntl("tools");
  const searchInputRef = useRef<HTMLInputElement>(null);   // 当前选择区域
  const [searchVal, setSearchVal] = useState('');
  const [searchList, setSearchList] = useState<TagOption[]>([]);

  useEffect(() => {
    setSearchList(options);
  }, [options]);

  // 显示的tag标签数组
  const tagList = useMemo(() => {
    const newTagList: TagOption[] = [];

    tagValue?.forEach((val) => {
      const tag = options?.find((item) => item?.value === val);
      tag && newTagList.push(tag);
    });
    return newTagList;
  }, [tagValue, isEn, options]);

  const handleClickSearchWrap = () => {
    searchInputRef.current?.focus();
  };

  return (
    <Dropdown
      className='aigc-dropdown'
      button={
        tagList?.length === 0 ? <div className='aigc-dropdown-placeholder'>{placeholder}</div> :
          tagList?.map((tag, index: number) => {
            const text = isEn ? tag?.text_en : tag?.text_cn;
            return index === tagList?.length - 1 ? text : `${text  }, `;
          })
        // <Tag.Group>
        //   {tagList?.map((tag, index) => (
        //     <Tag
        //       key={index}
        //       onClose={(e) => {
        //         setTagValue(tagValue?.filter(v => v !== tag?.value))
        //         check && check()
        //         e.stopPropagation()
        //       }}
        //     >
        //       {isEn ? tag?.text_en : tag?.text_cn}
        //     </Tag>
        //   ))}
        // </Tag.Group>
      }
      size="full"
      placement={'bottom'}
      matchButtonWidth={true}
      appearance="button"
      onClear={() => {
      }}
      onOpen={() => {
      }}
      onOpenChange={() => {
      }}
      onClose={() => {
        check?.();
      }}
      clickClose={false}
    >
      {( /* close */ ) => (
        <div className='aigc-dropdown-box'>
          {/* <SearchBox
            value={searchVal}
            onChange={(e) => {
              const value = e.replace(/(^\s*)|(\s*$)/g, '');
              if ('' != value) {
                const data = fuzzyQuery(JSON.parse(JSON.stringify(options)), value);
                setSearchVal(e);
                setSearchList(data)
              } else {
                setSearchVal(e);
                setSearchList(options);
              }
            }}
          /> */}
          {/* search */}
          <div className="aigc-dropdown-box-search-container">
            <div className="aigc-dropdown-box-search-wrap">
              <div className="aigc-dropdown-box-search-input-wrap">
                <div
                  className="aigc-dropdown-box-search-input-wrap-inner"
                  onClick={handleClickSearchWrap}
                >
                  {tagList?.map((tag, index) => (
                    <Tag
                      key={index}
                      onClose={(e) => {
                        setTagValue?.(tagValue?.filter((v) => v !== tag?.value) ?? []);
                        check?.();
                        e.stopPropagation();
                      }}
                    >
                      {isEn ? tag?.text_en : tag?.text_cn}
                    </Tag>
                  ))}
                  <Input
                    // placeholder={formatMessage({ id: 'search' })}
                    value={searchVal}
                    ref={searchInputRef}
                    className="dbc-region-select-search-input"
                    type="text"
                    onChange={(e) => {
                      const value = e.replace(/(^\s*)|(\s*$)/g, '');
                      if ('' !== value) {
                        const data = fuzzyQuery(JSON.parse(JSON.stringify(options)), value);
                        setSearchVal(e);
                        setSearchList(data);
                      } else {
                        setSearchVal(e);
                        setSearchList(options);
                      }
                    }}
                  />
                </div>
                <div className="dbc-region-select-search-icon">
                  {tagList.length > 0 ? (
                    <Icon type="dismiss" onClick={() => {
                      setTagValue?.([]);
                    }} />
                  ) : null}
                  <Icon type="search" />
                </div>
              </div>
            </div>
          </div>


          <Checkbox.Group
            value={tagValue?.map(String)}
            onChange={(value: string[]) => {
              setTagValue?.(value.map(Number));
            }}
            layout="column"
          >
            {searchList?.length > 0 ? searchList?.map((v) => (
              <Checkbox name={String(v?.value)} key={v?.value}>
                {keyFilter(v?.text, searchVal)}
              </Checkbox>
            )) :
              <div style={{ fontSize: '12px', textAlign: 'center', height: '40px', lineHeight: '40px' }}>
                No data yet
              </div>
            }
          </Checkbox.Group>
        </div>
      )}
    </Dropdown>
  );
}
